import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { useIntl } from 'react-intl'
import SystemStatusRow from './SystemStatusRow'
import { messages } from './messages'
import { useSystemStatusContext } from './SystemStatusProvider'

const MAX_ROWS = 3

const SystemStatusBanner = () => {
  const intl = useIntl()
  const { visibleMessages, dismiss } = useSystemStatusContext()
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [showAll, setShowAll] = useState(false)

  const toggle = (id: number) =>
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const hiddenCount = Math.max(0, visibleMessages.length - MAX_ROWS)
  const shownMessages = useMemo(
    () => (showAll ? visibleMessages : visibleMessages.slice(0, MAX_ROWS)),
    [visibleMessages, showAll]
  )

  if (visibleMessages.length === 0) {
    return null
  }

  return (
    <Box
      role="region"
      aria-label={intl.formatMessage(messages.region)}
      sx={{
        // Full page width.
        width: '100%',
        maxHeight: '25vh',
        overflowY: 'auto'
      }}
    >
      {shownMessages.map(message => (
        <SystemStatusRow
          key={message.id}
          message={message}
          expanded={expandedIds.has(message.id)}
          onToggle={toggle}
          onDismiss={dismiss}
        />
      ))}
      {hiddenCount > 0 && !showAll && (
        <Button
          variant="text"
          size="small"
          onClick={() => setShowAll(true)}
          sx={{
            m: '4px 8px',
            fontWeight: 500,
            fontSize: 12,
            letterSpacing: '.03em'
          }}
        >
          {intl.formatMessage(messages.more, { count: hiddenCount })}
        </Button>
      )}
    </Box>
  )
}

export default SystemStatusBanner
